<template>
    <form ref="elementRef" @submit.prevent="$_helpers.submit">
        <slot></slot>
    </form>
</template>

<script setup>
import { ref, computed, nextTick, onMounted } from "vue";

import useVuelidate from '@vuelidate/core';

const props = defineProps(
{
    steps               : { type: Object, required: false, default: undefined },
    data                : { type: Object, required: true },
    rules               : { type: Function, required: true },
    beforeSubmit        : { type: Function, required: false },
    afterSubmit         : { type: Function, required: false },
    submitButton        : { required: true },
    forceValidate       : { type: Boolean, required: false, default: false },
    manualSuccessHandle : { type: Boolean, required: false, default: false }
});

const emit = defineEmits(['update:forceValidate']);

const elementRef = ref( null );

const $externalResults = ref({});

const forceValidate = computed({ get() { return props.forceValidate }, set( value ) { emit( 'update:forceValidate', value ) } });

const v$ = useVuelidate( computed( props.rules ), props.data, { $externalResults } );

const $_helpers =
{
    $_ObjectGet: ( obj, path ) =>
    {
        if( obj && path )
        {
            let keys = path.split('.');

            return keys.length === 1 ? obj[keys[0]] : $_helpers.$_ObjectGet( obj[keys[0]], keys.slice(1).join('.') );
        }
    },

    $_ScrollIntoView: () =>
    {
        nextTick( () =>
        {
            if( props.steps )
            {
                let current_step_form = elementRef?.value?.querySelector('[data-step="'+props.steps.current+'"]');

                if( current_step_form?.querySelector('label.error') )
                {
                    return current_step_form.querySelector('label.error')?.scrollIntoView({ block: "nearest", behavior: "smooth" });
                }

                for( let stepElement of Array.from( elementRef?.value?.querySelectorAll('[data-step]') || [] ) )
                {
                    if( stepElement?.querySelector('label.error') ) { forceValidate.value = true; props.steps.update( parseInt( stepElement.dataset.step ) ); break; }
                }
            }
            else
            {
                elementRef?.value?.querySelector('.error')?.scrollIntoView({ block: "nearest", behavior: "smooth" });
            }
        })
    },

    $_handleSubmitAnimation: () =>
    {
        if( !props.steps || props?.steps?.isLast )
        {
            props.submitButton.load('success');
        }
    },

    error: ( path ) =>
    {
        return $_helpers.$_ObjectGet( v$.value, path )?.$errors?.[0]?.$message;
    },

    submit: async () =>
    {
        let hasButtonInside = elementRef.value.querySelector( 'button[type="submit"]' );

        if( props?.beforeSubmit ) { await props.beforeSubmit(); }

        //if( !props.steps || props?.steps?.isLast ) { props.submitButton.load('start'); }

                             v$.value.$clearExternalResults();
                             v$.value.$reset();
        const result = await v$.value.$validate();

        if( !result )
        {
            console.warn('Form validation error', v$.value);

            if( !hasButtonInside )
            {
                setTimeout( () => $_helpers.$_ScrollIntoView(), 1000 );

                throw { message: 'Form validation error', data: v$.value };
            }
            else
            {
                return props.submitButton.load('error', () => $_helpers.$_ScrollIntoView() );
            }
        }

        if( props.steps && !props.steps.isLast )
        {
            props.submitButton.handleCallback = false;

            props.steps.next();

            nextTick( () => props.submitButton.handleCallback = true );

            return;
        }

        if( !props?.afterSubmit )
        {
            if( !hasButtonInside )
            {
                return true;
            }
            else
            {
                return $_helpers.$_handleSubmitAnimation();
            }
        }

        try
        {
            if( hasButtonInside )
            {
                props.submitButton.load('start');
            }

            await props.afterSubmit( props.data );

            if( !props.manualSuccessHandle )
            {
                if( !hasButtonInside )
                {
                    return true;
                }
                else
                {
                    return $_helpers.$_handleSubmitAnimation();
                }
            }
        }
        catch( error )
        {
            if( !hasButtonInside )
            {
                throw { message: 'Form submit error', data: error };
            }
            else
            {
                return props.submitButton.load('error');
            }
        }
    }
}

onMounted( () =>
{
    elementRef.value.addEventListener('keydown', ( event ) =>
    {
        let $attr = event.target.getAttribute('type');
        let $node = event.target.nodeName.toLowerCase();

        if(
            $attr === 'button' ||
            $attr === 'submit' ||
            $node === 'textarea' ||
            event.target.getAttribute('contenteditable') === 'true'
        )
        {
            return true;
        }

        if( event.keyCode === 13 ){ event.preventDefault(); return false; } //TODO NEED TO TEST
    });

    if( forceValidate.value )
    {
        nextTick( async () =>
        {
                  v$.value.$clearExternalResults();
                  v$.value.$reset();
            await v$.value.$validate();

            $_helpers.$_ScrollIntoView();

            forceValidate.value = false;
        })
    }
})

defineExpose({ element: elementRef, v$, $externalResults, error: $_helpers.error, submit: $_helpers.submit });
</script>